import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Paper from '@mui/material/Paper';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import ShopifyIntegration from '../Integration/Marketplace/PlatformIntegrations/Shopify/ShopifyIntegration'
import DesktopshipperIntegration from '../Integration/Marketplace/PlatformIntegrations/Desktopshipper/DesktopshipperIntegration'
import BrightpearlIntegration from '../Integration/Marketplace/PlatformIntegrations/Brightpearl/BrightpearlIntegration'
import WoocommerceIntegration from '../Integration/Marketplace/PlatformIntegrations/Woocommerce/WoocommerceIntegration'
import BigcommerceIntegration from '../Integration/Marketplace/PlatformIntegrations/Bigcommerce/BigcommerceIntegration'
import TwoShipIntegration from '../Integration/Marketplace/PlatformIntegrations/Twoship/TwoShipIntegration'
import MagentoIntegration from '../Integration/Marketplace/PlatformIntegrations/Magento/MagentoIntegration'
import CentricIntegration from '../Integration/Marketplace/PlatformIntegrations/Centric/CentricIntegration'
import SkuLabsIntegration from '../Integration/Marketplace/PlatformIntegrations/SkuLabs/SkuLabsIntegration'
import EmailIntegration from '../Integration/Notification/PlatformIntegrations/Email/EmailIntegration'
import WebhookIntegration from '../Integration/Notification/PlatformIntegrations/Webhook/WebhookIntegration'
import KlaviyoIntegration from '../Integration/Notification/PlatformIntegrations/Klaviyo/KlaviyoIntegration'
import ShipStationIntegration from '../Integration/Marketplace/PlatformIntegrations/ShipStation/ShipStationIntegration'
import EtsyIntegration from '../Integration/Marketplace/PlatformIntegrations/Etsy/EtsyIntegration'
import ZonosIntegration from '../Integration/Marketplace/PlatformIntegrations/Zonos/ZonosIntegration'
import FishbowlIntegration from '../Integration/Marketplace/PlatformIntegrations/Fishbowl/FishbowlIntegration'

import { resetShopifyState } from '../Integration/Marketplace/PlatformIntegrations/Shopify/shopifyappSlice';
import { resetSkulabsState } from '../Integration/Marketplace/PlatformIntegrations/SkuLabs/skulabsSlice';
import { resetWoocommerceState } from '../Integration/Marketplace/PlatformIntegrations/Woocommerce/woocommerceSlice';
import { resetBrightpearlState } from '../Integration/Marketplace/PlatformIntegrations/Brightpearl/brightpearlSlice';
import { resetZonosState } from '../Integration/Marketplace/PlatformIntegrations/Zonos/zonosSlice';
import { resetFishbowlState } from '../Integration/Marketplace/PlatformIntegrations/Fishbowl/fishbowlSlice'; // Imported state reset
import WidthMaximize from './WidthMaximize'

const PlatformDrawer = ({ visible, platform, storeName, storeId, stepNumber, formDataValues, onClose }) => {
    const dispatch = useDispatch();
    const shopifyProgress = useSelector(state => state.shopifyapp && state.shopifyapp.progress && state.shopifyapp.progress);
    const brightpearlProgress = useSelector(state => state.brightpearl && state.brightpearl.progress);
    const woocommerceProgress = useSelector(state => state.woocommerce && state.woocommerce.progress);
    const bigcommerceProgress = useSelector(state => state.bigcommerce && state.bigcommerce.progress);
    const skulabsProgress = useSelector(state => state.skulabs && state.skulabs.progress);
    const zonosProgress = useSelector(state => state.zonos && state.zonos.progress);
    const progress = useSelector(state => state.klaviyo && state.klaviyo.progress);
    const shipstationprogress = useSelector(state => state.shipstation && state.shipstation.progress);
    const etsyProgress = useSelector(state => state.etsy && state.etsy.progress);
    const etsyconfigdetails = useSelector(state => state.etsy && state.etsy.configure);
    
    // --- CONNECTED FISHBOWL LIFECYCLE PROGRESS STATES ---
    const fishbowlProgress = useSelector(state => state.fishbowl && state.fishbowl.progress);
    const fishbowlConfigure = useSelector(state => state.fishbowl && state.fishbowl.configure);

    const [drawerWidth, setDrawerWidth] = useState("80%");
    const [drawer, setDrawer] = useState(true);

    const ondrawerclose = () => {
        dispatch(resetShopifyState());
        dispatch(resetSkulabsState());
        dispatch(resetZonosState());
        dispatch(resetWoocommerceState());
        dispatch(resetBrightpearlState());
        dispatch(resetFishbowlState()); // Force context garbage cleanup on integration exit
        setDrawer(false);
        onClose(false);
    };

    // --- CONNECTED AUTO-CLOSE TIMEOUT SYSTEM ---
    useEffect(() => {
        const normalizedPlatform = platform?.toUpperCase();

        // ONLY execute auto-close sequence if the user explicitly clicked the verification button
        if (visible && normalizedPlatform === 'FISHBOWL' && fishbowlProgress?.isApproved === true) {
            
            if (window.isFishbowlManualVerifyAction === true) {
                const autoCloseTimeout = setTimeout(() => {
                    // Reset action switches immediately
                    window.isFishbowlManualVerifyAction = false;
                    
                    dispatch(resetFishbowlState()); 
                    ondrawerclose();
                }, 850);

                return () => clearTimeout(autoCloseTimeout);
            }
        } else {
            // Clean action references on drawer closure configurations
            window.isFishbowlManualVerifyAction = false;
        }
    }, [fishbowlProgress?.isApproved, visible, platform, dispatch]);

    const params = new URLSearchParams(window.location.search) // id=123
    let platformname = params.get('platform')
    let storeEtsy=''
    if(platformname==='Etsy'){
        storeEtsy= params.get('store')
    }
    let storeParam = params.get('store_url')
    return (
        <>
            <Drawer
                anchor="right"
                open={visible}
                onClose={(event, reason) => {
                    if (reason !== "backdropClick") {
                        ondrawerclose();
                    }
                }}
                PaperProps={{
                    sx: { width: drawerWidth },
                }}
            >
                <div style={{ padding: "0 30px" }}>
                    <Card 
                        className="sc--cardhead--details"
                        sx={{ 
                            width: '100%', 
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #E2E8F0',
                            borderRadius: '16px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
                            overflow: 'hidden',
                            mb: 3,
                            mt:3
                        }}
                    >
                        <CardHeader 
                            title={
                                <Typography variant="h6" component="h2" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px', m: 0 }}>
                                    {`${platform} Integration`}
                                </Typography>
                            } 
                            action={
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ height: '38px' }}>
                                    
                                    {/* 1. Canvas Layout Frame Sizing Modulation Utilities */}
                                    <WidthMaximize
                                        defaultWidth="80%"
                                        onToggle={(newWidth) => setDrawerWidth(newWidth)}
                                    />
                                    
                                    {/* 2. Window Intercept Dismissal Operation Action */}
                                    <IconButton 
                                        aria-label="settings" 
                                        onClick={ondrawerclose}
                                        sx={{ 
                                            color: '#64748B',
                                            padding: '8px',
                                            borderRadius: '10px',
                                            border: '1px solid #E2E8F0',
                                            backgroundColor: '#FFFFFF',
                                            transition: 'all 0.15s ease-in-out',
                                            '&:hover': { 
                                                backgroundColor: '#F1F5F9', 
                                                borderColor: '#CBD5E1', 
                                                color: '#1E293B' 
                                            }
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: '18px' }} />
                                    </IconButton>

                                </Stack>
                            }
                            sx={{ p: 2.5, backgroundColor: '#FFFFFF' }}
                        />
                        
                        {/* Comprehensive Multi-Platform Channel Integration Loading Progression Bar Tracker */}
                        {((shopifyProgress && shopifyProgress.loading === true) || (brightpearlProgress && brightpearlProgress.loading === true) ||
                            (woocommerceProgress && woocommerceProgress.loading === true) || (bigcommerceProgress && bigcommerceProgress.loading === true) || 
                            (skulabsProgress && skulabsProgress.loading === true) || (progress && progress.loading === true) || (shipstationprogress && shipstationprogress.loading === true)
                            || (etsyProgress && etsyProgress.loading === true) || (etsyconfigdetails && etsyconfigdetails.loading === true) || (zonosProgress && zonosProgress.loading === true)
                            || (fishbowlProgress && fishbowlProgress.loading === true) || (fishbowlConfigure && fishbowlConfigure.loading === true)) && (
                                <LinearProgress 
                                    sx={{ 
                                        height: '3px', 
                                        backgroundColor: '#E2E8F0', 
                                        '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } 
                                    }} 
                                />
                            )
                        }
                        
                        <Divider sx={{ borderColor: '#E2E8F0' }} />
                    </Card>
                    {(platform === 'Shopify APP' || platform === 'SHOPIFY_APP' || platform === 'SHOPIFY') &&
                        <ShopifyIntegration storeName={storeName} storeId={storeId} stepNumber={stepNumber} />
                    }
                    {platform === 'Desktop Shipper' &&
                        <DesktopshipperIntegration />
                    }
                    {(platform === 'Brightpearl' || platform === 'brightpearl' || platform === 'BRIGHTPEARL') &&
                        <BrightpearlIntegration storeName={storeName} />
                    }
                    {(platform === 'WooCommerce' || platformname === 'woocommerce' || platform === 'WOOCOMMERCE') &&
                        <WoocommerceIntegration storeName={storeName} platformName={platformname} storeParam={storeParam} />
                    }
                    {(platform === 'Bigcommerce' || platformname === 'bigcommerce' || platform === 'BIGCOMMERCE' )&&
                        <BigcommerceIntegration storeName={storeName}  platformName={platform}/>
                    }
                    {(platform === 'Skulabs' || platformname === 'skulabs' || platform === 'SKULABS' )&&
                        <SkuLabsIntegration storeName={storeName}  platformName={platform}/>
                    }
                    {(platform === 'ShipStation' || platform === 'SHIPSTATION')&&
                        <ShipStationIntegration storeName={storeName}  platformName={platform}/>
                    }
                    {(platform === 'Etsy' || platform === 'ETSY')&&
                        <EtsyIntegration storeName={storeName} storeEtsy={storeEtsy} platformName={platform}/>
                    }
                    {platform === '2Ship' &&
                        <TwoShipIntegration />
                    }
                    {platform === 'Magento' &&
                        <MagentoIntegration />
                    }
                    {(platform === 'Email' || platform === 'EMAIL') &&
                        <EmailIntegration />
                    }
                    {(platform === 'Webhook' || platform === 'WEBHOOK') &&
                        <WebhookIntegration />
                    }
                    {(platform === 'Klaviyo') &&
                        <KlaviyoIntegration />
                    }
                    {(platform === 'Api') &&
                        <CentricIntegration />
                    }
                    {(platform === 'Zonos' || platform === 'ZONOS') &&
                        <ZonosIntegration storeName={storeName} storeId={storeId}/>
                    }
                    {(platform === 'Fishbowl' || platform === 'FISHBOWL') &&
                        <FishbowlIntegration storeName={storeName} storeId={storeId}/>
                    }
                </div>
            </Drawer >
        </>
    )
}

export default PlatformDrawer;